/**
 * Creating a recipe, as a place rather than as a button.
 *
 * ## Why creation lives on a route
 *
 * "New recipe", "Create your first recipe" and "Try a recipe" used to create
 * the document and *then* navigate. The store writes synchronously and the
 * list reads it through `useSyncExternalStore`, so the list re-rendered with
 * the new row on it and only afterwards did the router commit
 * `/recipes/$recipeId` — a frame of the list with a recipe in it that nobody
 * asked to see, and on an empty workspace a frame where the empty state is
 * replaced by a single "Untitled recipe" row before it all disappears again.
 *
 * Moving the create onto its own entry puts the navigation first. By the time
 * the recipe exists the list is unmounted, so there is no list left to paint
 * it on. This component renders nothing at all: it is one effect and a
 * redirect.
 *
 * ## `replace`, and the ticket
 *
 * Both exits replace rather than push, so Back from the new recipe reaches the
 * list the brewer came from. A pushed entry would leave `/recipes/new` behind,
 * and Back onto it would create a *second* recipe on the way past.
 *
 * The list stamps the entry it pushes here with its own position
 * (PLAN-LIST-STATE D-LS7), and this entry hands that ticket straight on
 * (D-LS8, the same sideways-move case as the editor replacing itself with a
 * duplicate): the recipe is not a step deeper than `/recipes/new`, it *is*
 * `/recipes/new`, once. So "My Recipes" returns to the search, chips and
 * scroll the brewer left rather than to a bare list.
 *
 * ## Exactly once
 *
 * The ref guard is not defensive coding. React StrictMode mounts, tears down
 * and mounts again in development (see `store/strict-mode.test.tsx` for what
 * that already cost once), and an unguarded effect would make two recipes
 * every time and open one of them.
 */

import type { SearchSchemaInput } from '@tanstack/react-router';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useForwardedTicket } from '@/lib/return-ticket';
import { parseSearchFlag } from '@/lib/search-params';
import { useRecipeActions } from '@/store/recipe-actions';

/** What `/recipes/new` reads out of its own URL. */
export interface NewRecipeSearch {
  /**
   * `?sample` — make the Sierra Nevada clone (`lib/sample-recipe.ts`) instead
   * of a blank recipe. `true | undefined` rather than `boolean`, because a
   * blank recipe is the default and is not worth a character in the address
   * bar; see `parseSearchFlag`.
   */
  readonly sample: true | undefined;
}

export const Route = createFileRoute('/recipes/new')({
  validateSearch: (search: { readonly sample?: unknown } & SearchSchemaInput): NewRecipeSearch => ({
    sample: parseSearchFlag(search.sample),
  }),
  component: NewRecipePage,
});

function NewRecipePage() {
  const { sample } = Route.useSearch();
  const navigate = useNavigate();
  const { createRecipe, createSampleRecipe } = useRecipeActions();
  const forwarded = useForwardedTicket();
  /** One recipe per visit, however often StrictMode re-runs this effect. */
  const created = useRef(false);

  useEffect(() => {
    if (created.current) return;
    created.current = true;
    const recipeId = sample === true ? createSampleRecipe() : createRecipe();
    // `null` is a refusal the brewer has already been told about — a guest at
    // the free cap, where `useDispatch` raised the upgrade dialog. There is no
    // recipe to open, and this entry cannot stay on a page that renders
    // nothing, so it becomes the list it was pressed from.
    if (recipeId === null) {
      void navigate({ to: '/recipes', replace: true });
      return;
    }
    void navigate({
      to: '/recipes/$recipeId',
      params: { recipeId },
      replace: true,
      state: forwarded,
    });
  }, [sample, createRecipe, createSampleRecipe, navigate, forwarded]);

  return null;
}
